/** @format */

// API service for backend communication

const API_BASE = "https://ask-api.kewt-p.co.uk/api/v1";

class ApiService {
  constructor() {
    this.baseUrl = API_BASE;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `HTTP ${response.status}`);
    }

    if (response.status === 204) return null;
    return response.json();
  }

  // ==================== User API ====================

  async loginUser(userData) {
    return this.request("/users/login", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async getUser(userId) {
    return this.request(`/users/${userId}`);
  }

  async getUserPreferences(userId) {
    return this.request(`/users/${userId}/preferences`);
  }

  async updateUserPreferences(userId, preferences) {
    return this.request(`/users/${userId}/preferences`, {
      method: "PUT",
      body: JSON.stringify(preferences),
    });
  }

  // ==================== Chat API ====================

  async createChat(userId, title = "New Chat") {
    return this.request("/chats/", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, title }),
    });
  }

  async getUserChats(userId) {
    const response = await this.request(`/chats/user/${userId}`);
    return response.chats || [];
  }

  async getChat(chatId, userId) {
    return this.request(`/chats/${chatId}?user_id=${userId}`);
  }

  async updateChat(chatId, userId, title) {
    return this.request(`/chats/${chatId}?user_id=${userId}`, {
      method: "PUT",
      body: JSON.stringify({ title }),
    });
  }

  async deleteChat(chatId, userId) {
    return this.request(`/chats/${chatId}?user_id=${userId}`, {
      method: "DELETE",
    });
  }

  // ==================== Message API ====================

  async createMessage(chatId, userId, content, role = "user") {
    return this.request(`/messages/?user_id=${userId}`, {
      method: "POST",
      body: JSON.stringify({ chat_id: chatId, content, role }),
    });
  }

  async getChatMessages(chatId, limit = 100) {
    return this.request(`/messages/chat/${chatId}?limit=${limit}`);
  }

  async updateMessageResponseTime(messageId, chatId, responseTime) {
    return this.request(
      `/messages/${messageId}/sources?chat_id=${chatId}&response_time=${responseTime}`,
      {
        method: "PUT",
        body: JSON.stringify(null), // sources can be null
      },
    );
  }

  async getRecentMessages(chatId, limit = 10) {
    return this.request(`/messages/chat/${chatId}/recent?limit=${limit}`);
  }

  /**
   * Get AI response for a user message (streaming)
   * @param {string} chatId - Chat ID
   * @param {string} userId - User ID
   * @param {string} message - User's message content
   * @param {function} onChunk - Callback function called for each chunk of streamed text
   * @returns {Promise<{reply: string}>} - AI's response
   */
  async getAIResponse(chatId, userId, message, onChunk = null) {
    // This calls the streaming chat endpoint which returns Server-Sent Events
    const response = await fetch(`${this.baseUrl}/stream/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        chat_id: chatId.toString(),
        user_id: userId,
        message: message,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `HTTP ${response.status}`);
    }

    // Handle streaming response (Server-Sent Events)
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = "";
    let buffer = ""; // Buffer for incomplete lines

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process complete lines from buffer
        const lines = buffer.split("\n");

        // Keep the last incomplete line in the buffer
        buffer = lines[lines.length - 1];

        // Process all complete lines
        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();

          // SSE format: "data: <content>"
          if (line.startsWith("data: ")) {
            const data = line.substring(6); // Remove "data: " prefix

            if (
              data === "[STREAM_COMPLETE]" ||
              data === "[STREAM_COMPLETE_RELOAD]"
            ) {
              // Streaming is complete
              // If it's RELOAD, signal frontend to reload from database
              return {
                reply: fullResponse,
                shouldReload: data === "[STREAM_COMPLETE_RELOAD]",
              };
            } else if (data.startsWith("[FORMATTED]")) {
              // Backend sent formatted version - parse JSON and replace fullResponse with it
              try {
                const formattedData = JSON.parse(data.substring(11)); // Remove "[FORMATTED]" prefix and parse JSON
                return {
                  reply: formattedData.content,
                  messageId: formattedData.message_id,
                  isFormatted: true,
                };
              } catch (e) {
                console.error("Failed to parse formatted response:", e);
                return { reply: fullResponse };
              }
            } else if (data === "[ERROR]") {
              throw new Error("Streaming error from server");
            } else if (data.startsWith("[ERROR]")) {
              throw new Error(data);
            } else if (data) {
              // Regular response chunk - call the callback if provided
              // Ensure data is a string
              const textChunk = typeof data === "string" ? data : String(data);
              fullResponse += textChunk;
              if (onChunk && typeof onChunk === "function") {
                onChunk(textChunk);
              }
            }
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        const line = buffer.trim();
        if (line.startsWith("data: ")) {
          const data = line.substring(6);
          if (data && data !== "[STREAM_COMPLETE]") {
            const textChunk = typeof data === "string" ? data : String(data);
            fullResponse += textChunk;
            if (onChunk && typeof onChunk === "function") {
              onChunk(textChunk);
            }
          }
        }
      }

      // If we finish without explicit completion signal
      return { reply: fullResponse };
    } catch (error) {
      reader.cancel();
      throw error;
    }
  }

  // ==================== Attachment API ====================

  async uploadAttachment(chatId, file, messageId = null) {
    const formData = new FormData();
    formData.append("file", file);

    let url = `${this.baseUrl}/attachments/?chat_id=${chatId}`;
    if (messageId) url += `&message_id=${messageId}`;

    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return response.json();
  }

  async getChatAttachments(chatId) {
    return this.request(`/attachments/chat/${chatId}`);
  }

  async downloadAttachment(attachmentId, chatId) {
    const response = await fetch(
      `${this.baseUrl}/attachments/${attachmentId}/download?chat_id=${chatId}`,
    );
    if (!response.ok) throw new Error("Download failed");
    return response.blob();
  }

  async deleteAttachment(attachmentId, chatId) {
    return this.request(`/attachments/${attachmentId}?chat_id=${chatId}`, {
      method: "DELETE",
    });
  }
}

export const api = new ApiService();
export default api;
